import * as t from 'io-ts';

import {enforceType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ApplicationResourceIdBrand} from '../../applications/bridge/v2/types/bridgeTypesV2';

/*
 * Runtime type.
 */

type PublicIdBrandNames = {
  activity: 'publicIdActivity';
  attachment: 'publicIdAttachment';
  calendarEvent: 'publicIdCalendarEvent';
  calendarEventVersion: 'publicIdCalendarEventVersion';
  card: 'publicIdCard';
  cardGroup: 'publicIdCardGroup';
  channel: 'publicIdChannel';
  circle: 'publicIdCircle';
  comment: 'publicIdComment';
  conversation: 'publicIdConversation';
  customField: 'publicIdCustomField';
  inbox: 'publicIdInbox';
  message: 'publicIdMessage';
  rule: 'publicIdRule';
  tag: 'publicIdTag';
  team: 'publicIdTeam';
  teammate: 'publicIdTeammate';
  topic: 'publicIdTopic';
};

export type ResourceNames = keyof PublicIdBrandNames;

const publicIdBrandNames: PublicIdBrandNames = {
  activity: 'publicIdActivity',
  attachment: 'publicIdAttachment',
  calendarEvent: 'publicIdCalendarEvent',
  calendarEventVersion: 'publicIdCalendarEventVersion',
  card: 'publicIdCard',
  cardGroup: 'publicIdCardGroup',
  channel: 'publicIdChannel',
  circle: 'publicIdCircle',
  comment: 'publicIdComment',
  conversation: 'publicIdConversation',
  customField: 'publicIdCustomField',
  inbox: 'publicIdInbox',
  message: 'publicIdMessage',
  rule: 'publicIdRule',
  tag: 'publicIdTag',
  team: 'publicIdTeam',
  teammate: 'publicIdTeammate',
  topic: 'publicIdTopic',
};

const publicIdPrefixes: Record<ResourceNames, string> = {
  activity: 'evt',
  attachment: 'fil',
  calendarEvent: 'cvt',
  calendarEventVersion: 'cev',
  card: 'crd',
  cardGroup: 'grp',
  channel: 'cha',
  circle: 'cir',
  comment: 'com',
  conversation: 'cnv',
  customField: 'fld',
  inbox: 'inb',
  message: 'msg',
  rule: 'rul',
  tag: 'tag',
  team: 'tim',
  teammate: 'tea',
  topic: 'top',
};

export function makePublicIdType<T extends ResourceNames>(resourceType: T) {
  return t.brand(
    t.string,
    (id): id is t.Branded<string, Pick<ApplicationResourceIdBrand, PublicIdBrandNames[T]>> => {
      const [prefix, base36Id] = id.split('_');
      const base10Id = parseInt(base36Id, 36);
      return (
        prefix === publicIdPrefixes[resourceType] &&
        Number.isInteger(base10Id) &&
        base10Id.toString(36) === base36Id
      );
    },
    publicIdBrandNames[resourceType],
  );
}

/*
 * Type.
 */

export type PublicId<T extends ResourceNames> = t.Branded<
  string,
  Pick<ApplicationResourceIdBrand, PublicIdBrandNames[T]>
>;

/*
 * Helpers.
 */

export function buildPublicId<T extends ResourceNames>(resourceType: T, id: number) {
  const publicId = `${publicIdPrefixes[resourceType]}_${id.toString(36)}`;
  return enforceType(makePublicIdType(resourceType), publicId);
}

export function tryParsePublicId<T extends ResourceNames>(
  publicId: string,
  expectedResourceType?: T,
): number | undefined {
  const matches = publicId.match(/^([a-z]{3})_(\w+)$/);
  if (!matches) {
    return undefined;
  }

  if (expectedResourceType && publicIdPrefixes[expectedResourceType] !== matches[1]) {
    return undefined;
  }

  return parseInt(matches[2], 36);
}
